import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "./useAuth";

/**
 * /auth/callback route — Task 4.0a.
 *
 * Cognito Hosted UI redirects here after Google SSO completes, with the
 * authorization code in the URL. Amplify intercepts the redirect during
 * its module-init flow and exchanges the code for tokens automatically;
 * by the time this component mounts the Hub has already fired 'signedIn'
 * and AuthContext has populated. We show a brief "Signing you in..."
 * placeholder and route the user on.
 *
 * Return path: a page that kicks off sign-in can stash where to come back to
 * under `pt.postLoginRedirect` (e.g. the shared Tilt Report stashes its
 * /score/{id} path so signing in returns there, not to the home search).
 * sessionStorage survives the round-trip to Cognito in the same tab. Defaults
 * to home when nothing is stashed.
 *
 * If the redirect failed (the user denied consent, IdP error, etc.),
 * AuthContext sets `error` and we surface it here with a retry link.
 */

const POST_LOGIN_REDIRECT_KEY = "pt.postLoginRedirect";

export function AuthCallback() {
  const { user, loading, error } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      let dest = "/";
      try {
        const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        if (stored) {
          dest = stored;
          sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        }
      } catch {
        // sessionStorage unavailable (private mode / quota) — fall back to home.
      }
      navigate(dest, { replace: true });
    }
  }, [loading, user, navigate]);

  if (error) {
    return (
      <div className="app">
        <main>
          <h2>Sign-in failed</h2>
          <p>{error.message}</p>
          <p>
            <a href="/">Return home</a> and try again.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <main>
        <p>Signing you in…</p>
      </main>
    </div>
  );
}
